import { useState, useEffect, useRef, useCallback } from "react";
import type {
  TrackingState,
  TrackingConnectionStatus,
  TrackingBacktestState,
  SymbolPrice,
} from "../state/types";

const WS_PATH = "/ws/tracking";
const RECONNECT_INITIAL_MS = 2000;
const RECONNECT_MAX_MS = 30000;
const STALE_THRESHOLD_MS = 130000; // ~2 min if interval is 60s

const LOG_PREFIX = "[TrackingWS]";
function log(...args: unknown[]) {
  console.log(LOG_PREFIX, ...args);
}

function wsUrl(token: string | null): string | null {
  if (!token) return null;
  const base = typeof window !== "undefined" ? window.location : null;
  if (!base) return null;
  const protocol = base.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${base.host}${WS_PATH}?token=${encodeURIComponent(token)}`;
}

export type UseTrackingWebSocketResult = {
  state: TrackingState | null;
  connectionStatus: TrackingConnectionStatus;
  isStale: boolean;
  lastMessageAt: number | null;
};

export function useTrackingWebSocket(idToken: string | null): UseTrackingWebSocketResult {
  const [state, setState] = useState<TrackingState | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<TrackingConnectionStatus>("disconnected");
  const [lastMessageAt, setLastMessageAt] = useState<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef(RECONNECT_INITIAL_MS);
  const effectRunCountRef = useRef(0);
  const tokenRef = useRef(idToken);
  const intentionalCloseRef = useRef(false);
  tokenRef.current = idToken;

  const connect = useCallback(() => {
    const token = tokenRef.current;
    const url = wsUrl(token);
    if (!url) {
      setConnectionStatus("disconnected");
      return;
    }
    setConnectionStatus("connecting");
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      log("connected");
      setConnectionStatus("connected");
      reconnectDelayRef.current = RECONNECT_INITIAL_MS;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as { type?: string; monitoring_on?: boolean; has_cli?: boolean; backtests?: unknown[]; prices?: Record<string, unknown> };
        if (data.type === "state") {
          setState({
            monitoring_on: !!data.monitoring_on,
            has_cli: !!data.has_cli,
            backtests: (Array.isArray(data.backtests) ? data.backtests : []) as TrackingBacktestState[],
            prices: (data.prices && typeof data.prices === "object") ? (data.prices as Record<string, SymbolPrice>) : {},
          });
          setLastMessageAt(Date.now());
        }
      } catch {
        // ignore parse errors
      }
    };

    ws.onclose = (event: CloseEvent) => {
      log(
        "closed",
        "code=" + event.code,
        "reason=" + (event.reason || "(none)"),
        "wasClean=" + event.wasClean,
        "(1000=normal close from our cleanup, 1006=abnormal/connection lost)"
      );
      wsRef.current = null;
      setConnectionStatus("disconnected");
      if (intentionalCloseRef.current) {
        intentionalCloseRef.current = false;
        return;
      }
      if (tokenRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          log("reconnecting in", reconnectDelayRef.current, "ms");
          connect();
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current * 1.5,
            RECONNECT_MAX_MS
          );
        }, reconnectDelayRef.current);
      }
    };

    ws.onerror = () => {
      // Close will follow
    };
  }, []);

  const tokenStr = idToken ?? "";
  useEffect(() => {
    effectRunCountRef.current += 1;
    const runId = effectRunCountRef.current;
    log("effect run #" + runId);
    if (!idToken) {
      setConnectionStatus("disconnected");
      setState(null);
      return;
    }
    connect();
    return () => {
      log("effect cleanup #" + runId, "(closing socket if open)");
      intentionalCloseRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setConnectionStatus("disconnected");
    };
  }, [tokenStr, connect]);

  const isStale =
    connectionStatus === "connected" &&
    lastMessageAt != null &&
    Date.now() - lastMessageAt > STALE_THRESHOLD_MS;

  return { state, connectionStatus, isStale, lastMessageAt };
}
